/**
 * What a failed verification call means to the agent looking at the screen.
 *
 * The transport (verificationApi.ts) decides *whether* a call failed and with which stable
 * code; this module decides what the user is told. The message text is the portal's own —
 * chosen from the code, never taken from whatever prose arrived over the wire (PORT-ERROR).
 *
 * A failure is a plain, serializable object rather than an `Error` subclass, matching the
 * case-list convention, so it can be held in component state or put through a store without
 * surprises.
 */
import type { ApiErrorCode } from './contracts';

/** What the user is told, per code. Anything unrecognized falls back rather than being shown. */
const MESSAGE_FOR_CODE: Record<ApiErrorCode, string> = {
  InvalidRequest: 'That request could not be read. Check the details and try again.',
  NotFound: 'The requested record was not found.',
  Unauthenticated: 'Your session has expired. Sign in again to continue.',
  Conflict: 'This government email cannot be linked to your account. Please contact support.',
  RecordChanged: 'Your details changed while you were editing. Please submit again.',
  DownstreamRejection: 'The verification service would not accept this request. Please contact support.',
  VerificationCodeIncorrect: 'That code is incorrect. Check the code and try again.',
  VerificationCodeExpired: 'That code has expired. Request a new code to continue.',
  VerificationCooldown: 'A code was sent recently. Wait a moment before requesting another.',
  VerificationAttemptsExceeded: 'Too many incorrect codes were entered. Wait a while, then request a new code.',
  DownstreamThrottled: 'The verification service is busy right now. Wait a moment and try again.',
  DownstreamUnavailable: 'The verification service is unavailable right now. Please try again shortly.',
  Timeout: 'That took too long. Please try again shortly.',
  InvalidResponse: 'Something went wrong. Please contact support if this continues.',
  UnhandledException: 'Something went wrong. Please try again shortly.',
  Unknown: 'Something went wrong. Please try again shortly.',
};

/**
 * What the registration step says instead, where the verification text would mislead: its
 * `InvalidRequest` and `Conflict` text is about the email address, which this form does not ask
 * for. Codes not listed here use `MESSAGE_FOR_CODE`.
 */
const REGISTRATION_MESSAGE_FOR_CODE: Partial<Record<ApiErrorCode, string>> = {
  InvalidRequest: 'Some of the details were not accepted. Check them and try again.',
  Conflict:
    'Your details cannot be updated right now. Verify your government email again, or contact support if this continues.',
  DownstreamRejection: 'The registration service would not accept this request. Please contact support.',
};

/**
 * Codes that mean the code the screen is holding can no longer be checked.
 *
 * The code screen cannot recover from this by letting the user type again: the code it
 * would be checked against is past its lifetime, or its attempts are spent. The flow
 * returns to the address step rather than leaving the user retyping against nothing.
 */
const CHALLENGE_LOST_CODES: ReadonlySet<ApiErrorCode> = new Set<ApiErrorCode>([
  'VerificationCodeExpired',
  'VerificationAttemptsExceeded',
]);

export const FALLBACK_MESSAGE = 'Something went wrong. Please try again shortly.';

/** A transport-level failure that never reached a coded API answer. */
export type FailureCode = ApiErrorCode | 'TransportFailure';

/** A verification call that did not produce a result. */
export interface ApiFailure {
  code: FailureCode;
  /** The HTTP status the failure rode in on, or 0 when the call never completed. */
  status: number;
  /** The portal-owned message, already chosen from the code. */
  message: string;
}

export function messageForCode(code: string | undefined): string {
  if (code !== undefined && code in MESSAGE_FOR_CODE) {
    return MESSAGE_FOR_CODE[code as ApiErrorCode];
  }

  return FALLBACK_MESSAGE;
}

/** Builds the failure for a verification call that did not produce a result. */
export function verificationFailure(code: FailureCode, status: number): ApiFailure {
  const message = code === 'TransportFailure' ? FALLBACK_MESSAGE : messageForCode(code);
  return { code, status, message };
}

/** Builds the failure for a registration save that did not produce a result. */
export function registrationFailure(code: FailureCode, status: number): ApiFailure {
  const registrationMessage =
    code === 'TransportFailure' ? undefined : REGISTRATION_MESSAGE_FOR_CODE[code as ApiErrorCode];
  return registrationMessage === undefined
    ? verificationFailure(code, status)
    : { code, status, message: registrationMessage };
}

/** Whether a failure means the held challenge is no longer usable. */
export function isChallengeLost(code: string | undefined): boolean {
  return code !== undefined && CHALLENGE_LOST_CODES.has(code as ApiErrorCode);
}

/** Whether an unknown thrown value is one of our failures. */
export function isApiFailure(value: unknown): value is ApiFailure {
  return (
    typeof value === 'object' &&
    value !== null &&
    'code' in value &&
    'message' in value &&
    typeof (value as ApiFailure).message === 'string'
  );
}
